import { validate as uuidValidate } from 'uuid';
import { FilterType, SelectedFilterItem } from '@constants/data-table';

enum JoinType {
  Conjunction = ' and ',
  Disjunction = ' or ',
}

const mergeODataFiltersBy = (condition: JoinType, filters: (string | null | undefined)[]) => {
  const separator = condition as string;
  const validFilters = filters.filter((filter) => filter && filter.trim() !== '');

  if (!validFilters?.length) return '';

  return validFilters.join(separator);
};

export const normalizeODataStringsField = (field: string): string => {
  if (/\s/.test(field)) return `'${field}'`;
  return field;
};

const parseODataValue = (value: unknown) => {
  if (typeof value === 'string' && !uuidValidate(value)) {
    return `'${value}'`;
  }
  return value;
};

export const getODataEQFilterFrom = (field: string, value: unknown): string => {
  return `${normalizeODataStringsField(field)} eq ${parseODataValue(value)}`;
};

export const getODataNEQFilterFrom = (field: string, value: unknown): string => {
  return `${normalizeODataStringsField(field)} ne ${parseODataValue(value)}`;
};

export const getODataRangeFilterFrom = (field: string, value: number[]): string =>
  `${normalizeODataStringsField(field)} ge ${parseODataValue(
    value[0],
  )} and ${normalizeODataStringsField(field)} le ${parseODataValue(value[1])}`;

export const getODataDateFilterFrom = (
  field: string,
  value: unknown,
  comparator?: string,
): string => {
  comparator = comparator || 'ge';
  return `${normalizeODataStringsField(field)} ${comparator} '${value}'`;
};
export const getODataSearchFilterFrom = (field: string, value: string): string =>
  `contains(tolower(${normalizeODataStringsField(field)}), tolower('${value}'))`;

export const getODataMultiSelectFilterFrom = (field: string, value: string[]): string =>
  value.length === 1
    ? `${normalizeODataStringsField(field)} eq ${parseODataValue(value[0])}`
    : `${normalizeODataStringsField(field)} in (${value.map(parseODataValue).join(', ')})`;

const parseFilterByType = (field: string, filter: SelectedFilterItem[]) => {
  const [{ type }] = filter;
  const parseByTypeMap = {
    [FilterType.TOGGLE]: () => {
      if (!filter[0].value) return '';
      return getODataEQFilterFrom(field, filter[0].value);
    },
    [FilterType.SELECT]: () => getODataEQFilterFrom(field, filter[0].value),
    [FilterType.RANGE]: () => getODataRangeFilterFrom(field, filter[0].value as number[]),
    [FilterType.DATE]: () => getODataDateFilterFrom(field, filter[0].value),
    [FilterType.MULTI_SELECT]: () =>
      getODataMultiSelectFilterFrom(field, filter.map(({ value }) => value) as string[]),
  };

  return parseByTypeMap[type]();
};

export const getODataFilterFrom = <T extends Record<string, SelectedFilterItem[]>>(
  filters: T,
): string => {
  const filterKeys = Object.keys(filters);

  if (!filterKeys.length) return '';

  return filterKeys.reduce((parsedFilters, filterKey) => {
    const isFirstFilter = !parsedFilters;
    const filter = filters[filterKey];

    if (!filter.length) return parsedFilters;

    const parsedFilter = parseFilterByType(filterKey, filter);

    if (isFirstFilter) return parsedFilter;

    return `${parsedFilters} and ${parsedFilter}`;
  }, '');
};

export const getODataConjunctionFilterFrom = (...filters: (string | null | undefined)[]): string =>
  mergeODataFiltersBy(JoinType.Conjunction, filters);

export const getODataDisjunctionFilterFrom = (...filters: (string | null | undefined)[]): string =>
  mergeODataFiltersBy(JoinType.Disjunction, filters);

/**
 * @description Get OData filter for user or service key. In the feedback table we use the `users` filter for both real users and API keys.
 * The API keys have the format `api_key:${apiKeyId}` and the users have the format `${userId}`.
 *
 * @param filters - Record<string, SelectedFilterItem[]>
 * @returns string
 */
export const getODataFilterFromSpecial = <T extends Record<string, SelectedFilterItem[]>>(
  filters: T,
): string => {
  const { created_by: users, ...rest } = filters;

  const apiKey = users
    .filter(({ key }) => key.includes(':'))
    .map(({ key, value, ...remainder }) => ({
      ...remainder,
      key: key.split(':')[1].trim(),
      value: (value as string).split(':')[1].trim(),
    }));

  const createdBy = users.filter(({ key }) => !key.includes(':'));

  return getODataFilterFrom({
    ...rest,
    api_key: apiKey,
    created_by: createdBy,
  });
};
